import { create } from 'zustand';
import {
  type ChatEntry,
  type Phase,
  type PlayerId,
  type PrivateState,
  type RoomSnapshot,
} from '../../../shared/types';

interface GameStore {
  connected: boolean;
  error: string | null;
  roomCode: string | null;
  myPlayerId: PlayerId | null;
  identity: string | null;
  myName: string | null;
  phase: Phase;
  snapshot: RoomSnapshot | null;
  privateState: PrivateState | null;

  setConnected: (connected: boolean) => void;
  setError: (error: string | null) => void;
  setConnectionInfo: (info: { roomCode: string; playerId: PlayerId; identity: string }) => void;
  setSnapshot: (snapshot: RoomSnapshot) => void;
  setPrivateState: (state: PrivateState) => void;
  reset: () => void;
}

const SESSION_KEY = 'ttt-session';

/** Restores the current seat across page refreshes so ws.onopen can
 *  send RECONNECT (JOIN_ROOM would fail while the old seat is held). */
function loadSession(): {
  roomCode: string | null;
  myPlayerId: PlayerId | null;
  identity: string | null;
} {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return { roomCode: null, myPlayerId: null, identity: null };
    const s = JSON.parse(raw) as { roomCode?: string; playerId?: string; identity?: string };
    if (s.roomCode && s.playerId && s.identity) {
      return { roomCode: s.roomCode, myPlayerId: s.playerId, identity: s.identity };
    }
  } catch {
    /* corrupted session — ignore */
  }
  return { roomCode: null, myPlayerId: null, identity: null };
}

const stored = loadSession();

const empty = {
  connected: false,
  error: null as string | null,
  roomCode: null as string | null,
  myPlayerId: null as PlayerId | null,
  identity: null as string | null,
  myName: null as string | null,
  phase: 'lobby' as Phase,
  snapshot: null as RoomSnapshot | null,
  privateState: null as PrivateState | null,
};

export const useGameStore = create<GameStore>((set) => ({
  ...empty,
  ...stored,

  setConnected: (connected) => set({ connected }),
  setError: (error) => set({ error }),
  setConnectionInfo: ({ roomCode, playerId, identity }) => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ roomCode, playerId, identity }));
    } catch {
      /* storage unavailable — reconnect after refresh just won't work */
    }
    set({ roomCode, myPlayerId: playerId, identity });
  },
  setSnapshot: (snapshot) =>
    set({
      snapshot,
      phase: snapshot.phase,
    }),
  setPrivateState: (state) => set({ privateState: state }),
  reset: () => {
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* ignore */
    }
    set({ ...empty });
  },
}));

/** The player record for "me", or null before joining a room. */
export const useMe = () =>
  useGameStore((s) => {
    if (!s.snapshot || !s.myPlayerId) return null;
    return s.snapshot.players.find((p) => p.id === s.myPlayerId) ?? null;
  });

export const useMyPiece = () => useMe()?.piece ?? null;

export const useRoomCode = () => useGameStore((s) => s.roomCode);
export const usePhase = () => useGameStore((s) => s.phase);
export const useSnapshot = () => useGameStore((s) => s.snapshot);
export const usePrivateState = () => useGameStore((s) => s.privateState);
